import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { Box, ButtonBase, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { isPunchlessRow } from "@/components/StaffTable/utils";
import { HolidayShiftChip } from "@/components/team/Holidays/schedule/HolidayShiftChip";
import type { ScheduleHoliday } from "@/components/team/Holidays/schedule/scheduleHolidays";
import { CHARGE, FONT_SIZE, GAPS_LIST, INK, NEUTRAL, OVERTIME, STATUS, SURFACE } from "@/theme/tokens";
import MetaChip from "@/ui/Badges/MetaChip";
import { TOUCH_TARGET_SX } from "@/ui/touchTarget";
import { NurseTrainingStatus } from "@/utils/models";
import { GapsRoleBadge } from "./GapsRoleBadge";
import type { GapsStaffEntry } from "./gapsDayModel";
import { ROW_MARKER_SX } from "./gapsRowLayout";

type Props = {
	entry: GapsStaffEntry;
	/** Opens the person's actions sheet — the whole row's tap and its `⋯`. */
	onOpen: (nurseInShiftId: string) => void;
	/** Opens the assign-unit sheet for someone who is on the shift but not yet placed. */
	onAssignLocation: (nurseInShiftId: string) => void;
	/**
	 * This shift is one the week's cost projection flagged as driving overtime.
	 *
	 * A flag, not a number of hours: `costlyShiftsByDay` is the signal the staffing grid already marks
	 * OT from, and nothing on a roster row carries OT hours. The artifact prints "OT 4h"; the hours
	 * behind it come from the cost prediction the add-staff and approve flows run, so the mark here
	 * says only what the app can currently stand behind.
	 */
	isOvertime: boolean;
	/**
	 * The group's own job colour, drawn as the row's left edge. The missing rows under the same header
	 * carry the same 2px edge in red, so the group's rows read as one indented block under its band.
	 */
	accentColor: string;
	/** The holiday whose Apply put this shift on the schedule. Absent for every other shift, and for anyone who can't see holidays. */
	shiftHoliday?: ScheduleHoliday;
};

/**
 * One person on a shift.
 *
 * Everything the row carries is an exception: the unit, the hours when they differ from the group's,
 * overtime, a note, and a training mark. A row with none of them is just a name, which is the point —
 * the eye is meant to stop only where something is unusual.
 *
 * Orientation is drawn as an education cap with no word beside it, and trainee and education as small chips. The
 * artifact's note explains why: the old `orient` chip was read as a verb, while a cap is read once and
 * never again. Both are marks on a person who is present and not counted, which the group's own note
 * spells out in words once rather than on every row.
 */
export const GapsStaffRow: FC<Props> = ({ entry, onOpen, onAssignLocation, isOvertime, accentColor, shiftHoliday }) => {
	const { t } = useTranslation();
	const { slot, locationName, countsToCoverage, isInOverParLocation } = entry;
	const shift = slot.nurseInShift;
	const isUnassigned = locationName === "";

	return (
		<Box
			data-testid={`gaps-staff-row-${shift.id}`}
			// The whole row opens the person's actions, as the artifact's row does — a thumb aims at the name, not at a
			// 28px glyph. The `⋯` below stays the row's accessible button; this handler is the bigger target for touch.
			onClick={() => onOpen(shift.id)}
			// `pl` + the 2px edge is the missing row's own inset, so names stay on "Missing nurse"'s line.
			sx={{
				cursor: "pointer",
				display: "flex",
				alignItems: "center",
				gap: 1,
				pl: 1.25,
				pr: 0.5,
				py: 1.125,
				bgcolor: SURFACE,
				borderLeft: `${GAPS_LIST.EDGE_PX}px solid ${accentColor}`,
				borderBottom: `1px solid ${NEUTRAL[150]}`,
			}}
		>
			<GapsRoleBadge role={shift.role} size="row" />
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography
					sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: INK.PRIMARY, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
				>
					{slot.name}
				</Typography>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.75,
						mt: "1px",
						fontSize: FONT_SIZE.LABEL_XS,
						color: INK.MUTED,
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{isUnassigned ? (
						// Its own control, not the row's tap: it answers "where does this person work" directly, in the
						// assign-unit sheet, instead of going through the actions sheet first.
						<ButtonBase
							data-testid={`gaps-staff-assign-${shift.id}`}
							onClick={(event) => {
								event.stopPropagation();
								onAssignLocation(shift.id);
							}}
							sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 600, color: "primary.dark", textDecoration: "underline", textUnderlineOffset: "2px" }}
						>
							{t("gaps.row.assignUnit")}
						</ButtonBase>
					) : (
						// A unit already over its par reads in the desktop's overstaffed amber and says so: it is why the
						// group's missing rows can outnumber the header's shortfall (see `isInOverParLocation`).
						<Box
							component="span"
							data-testid={isInOverParLocation ? `gaps-staff-over-par-${shift.id}` : undefined}
							sx={{
								minWidth: 0,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								color: isInOverParLocation ? STATUS.WARNING.main : INK.SECONDARY,
								fontWeight: isInOverParLocation ? 600 : 500,
							}}
						>
							{isInOverParLocation ? `${locationName} · ${t("gaps.row.overPar")}` : locationName}
						</Box>
					)}
					{/* No hours to print, no separator — a bare "·" read as a value that failed to load. */}
					{slot.hoursLabel !== "" && (
						<>
							<Box component="span">·</Box>
							<Box component="span" sx={{ flex: "none" }}>
								{slot.hoursLabel}
							</Box>
						</>
					)}
				</Box>
			</Box>

			{shiftHoliday && <HolidayShiftChip group={shiftHoliday.group} dataTestId={`gaps-holiday-chip-${shift.id}`} />}
			{shift.charge && (
				<MetaChip
					label={t("gaps.row.charge")}
					size="small"
					customTone={{ bg: CHARGE.BG, ink: CHARGE.INK }}
					sx={ROW_MARKER_SX}
					dataTestId={`gaps-staff-charge-${shift.id}`}
				/>
			)}
			{shift.trainingStatus === NurseTrainingStatus.Orientation && (
				<MetaChip
					icon={<SchoolOutlinedIcon />}
					tone="primary"
					size="small"
					tooltip={t("gaps.row.orientation")}
					ariaLabel={t("gaps.row.orientation")}
					sx={ROW_MARKER_SX}
					dataTestId={`gaps-staff-orientation-${shift.id}`}
				/>
			)}
			{shift.trainingStatus === NurseTrainingStatus.Trainee && (
				<MetaChip label={t("gaps.row.trainee")} tone="primary" size="small" sx={ROW_MARKER_SX} dataTestId={`gaps-staff-trainee-${shift.id}`} />
			)}
			{/* Settable from the row's More sheet, so the row has to be able to say it is set. */}
			{shift.trainingStatus === NurseTrainingStatus.Education && (
				<MetaChip label={t("gaps.row.education")} tone="primary" size="small" sx={ROW_MARKER_SX} dataTestId={`gaps-staff-education-${shift.id}`} />
			)}
			{isOvertime && (
				<MetaChip
					label={t("gaps.row.overtime")}
					size="small"
					customTone={{ bg: OVERTIME.BG, ink: OVERTIME.INK }}
					sx={ROW_MARKER_SX}
					dataTestId={`gaps-staff-ot-${shift.id}`}
				/>
			)}
			{/* A past day shows what was punched (APP-886); each such row says so, so it is never read as the schedule. */}
			{slot.isActual && <MetaChip label={t("gaps.row.punch")} tone="success" size="small" sx={ROW_MARKER_SX} dataTestId={`gaps-staff-punch-${shift.id}`} />}
			{/* Scheduled, expected to clock in, and no punch came: the Actual table's "Missing Punch", on the person's own row. */}
			{slot.isScheduledStandIn && isPunchlessRow(shift) && (
				<MetaChip
					label={t("punchSummary.category.missingPunch")}
					tone="warning"
					size="small"
					sx={ROW_MARKER_SX}
					dataTestId={`gaps-staff-missing-punch-${shift.id}`}
				/>
			)}
			{shift.notes && <StickyNote2OutlinedIcon titleAccess={t("gaps.row.hasNote")} sx={{ fontSize: 13, color: INK.MUTED, flex: "none" }} />}
			{/* A person who does not count toward par is still on the shift; the row says so quietly so the
			    header's arithmetic is explicable without opening anything. */}
			{!countsToCoverage && (
				<Box
					component="span"
					data-testid={`gaps-staff-uncounted-${shift.id}`}
					sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: NEUTRAL[400], flex: "none" }}
				/>
			)}
			<ButtonBase
				data-testid={`gaps-staff-open-${shift.id}`}
				aria-label={t("gaps.row.openActions", { name: slot.name })}
				aria-haspopup="dialog"
				onClick={(event) => {
					// The row opens the same sheet; stopping here keeps one tap from opening it twice.
					event.stopPropagation();
					onOpen(shift.id);
				}}
				sx={{ ...TOUCH_TARGET_SX, width: 28, flex: "none", borderRadius: "7px", color: INK.MUTED, fontSize: FONT_SIZE.TITLE_SM }}
			>
				⋯
			</ButtonBase>
		</Box>
	);
};
